import { FC, Fragment, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSiteId } from 'features/topbar/selectedSiteSlice';
import {
  selectBudgetScenarioLabel,
  selectSelectedBudgetScenario,
  selectSelectedModelRunDetails,
  selectSelectedSegmentKey,
  selectSelectedTimeType,
  setSelectedModelRunDetails,
  setSelectedBudgetScenario,
  setSelectedSegmentKey,
  setSelectedTimeType
} from './campaign-mmm-control.slice';
import DropdownMenu from 'common/components/drop-downs/drop-down-menu';
import DropdownMenuButton from 'common/components/drop-downs/dropdown-menu-button';
import { selectAllUpdatedBudgetScenario } from 'features/marketing-mix-modelling/budget-scenario-planner/edit-budget-scenario.slice';
import { BudgetScenarioPlannerModel } from 'features/marketing-mix-modelling/budget-scenario-planner/models/budget-scenario-planner.model';
import { groupBy } from 'common/utils/array-helpers';

const getModelTypesLabel = (selectedModelTypes: string[]): string =>
  selectedModelTypes.length > 0
    ? selectedModelTypes.slice(0, 2).join(' / ')
    : 'Select Budget Scenario';

const CampaignMmmControls: FC = () => {
  const dispatch = useDispatch();
  const [isModelRunDetailsDropdownOpen, setModelRunDetailsDropdownOpen] =
    useState(false);

  const [isSegmentSelectorDropdownOpen, setSegmentSelectorDropdownOpen] =
    useState(false);

  const [isTimeRangeSelectorDropdownOpen, setTimeRangeSelectorDropdownOpen] =
    useState(false);

  const [
    isBudgetScenarioSelectorDropdownOpen,
    setBudgetScenarioSelectorDropdownOpen
  ] = useState(false);

  const siteId = useSelector(selectSiteId);
  const selectedSegmentKey = useSelector(selectSelectedSegmentKey);
  const selectedTimeType = useSelector(selectSelectedTimeType);
  const selectedBudgetScenario = useSelector(selectSelectedBudgetScenario);
  const selectedModelRunDetails = useSelector(selectSelectedModelRunDetails);

  const editedBudgetScenarioPlannerModels = useSelector(
    selectAllUpdatedBudgetScenario
  );

  const groupedFullBudgetScenariosForDropdown = useMemo(() => {
    if (
      !selectedModelRunDetails.modelRunName ||
      selectedModelRunDetails.modelRunName === '' ||
      !selectedModelRunDetails.targetDate
    ) {
      return [];
    }

    const groupedScenarios = groupBy(
      editedBudgetScenarioPlannerModels
        .concat(
          ['Optimised'].map((modelType, index) => {
            return {
              budgetId: `default_budget_${modelType}_${index}`,
              scenarioName: modelType,
              scenarioType: 'AI Generated',
              modelRunName: selectedModelRunDetails.modelRunName
            } as BudgetScenarioPlannerModel;
          })
        )
        .filter(
          (scenario) =>
            scenario.modelRunName === selectedModelRunDetails.modelRunName
        ),
      (scenario) => scenario.scenarioType
    );

    return Object.entries(
      groupedScenarios as Record<
        'Custom' | 'AI Generated',
        BudgetScenarioPlannerModel[]
      >
    );
  }, [
    selectedModelRunDetails.modelRunName,
    selectedModelRunDetails.targetDate,
    editedBudgetScenarioPlannerModels
  ]);

  // hardcoded for phase 1, will be replaced when actual data is available
  const availableSegments = ['Campaign Grouping'];
  const availableTimeTypes = ['8Weeks'];
  const availableModelRunDetails = [
    {
      targetDate: '2025-06-05',
      modelRunName: 'MMM Campaign Run'
    }
  ];

  const selectedBudgetScenarioLabel = useSelector(selectBudgetScenarioLabel);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium">Available Model Runs:</label>
        {availableModelRunDetails.length > 0 ? (
          <DropdownMenu
            isDropdownOpen={isModelRunDetailsDropdownOpen}
            containerClasses="flex justify-between relative mr-3"
            buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 flex-row-reverse px-3 py-2 bg-white"
            buttonIconClasses="ra-icons-generic-down-arrow text-bottom text-xs flex px-2"
            buttonLabel={
              !selectedModelRunDetails?.targetDate ||
              !selectedModelRunDetails.modelRunName
                ? 'Select Model Run'
                : `${selectedModelRunDetails.targetDate} ${selectedModelRunDetails.modelRunName}`
            }
            buttonLabelClasses=""
            hasExtraButtonIconClasses={false}
            dropdownMenuClasses="z-40 top-11"
            onDropdownClicked={() =>
              setModelRunDetailsDropdownOpen(!isModelRunDetailsDropdownOpen)
            }
            onClickAway={() => setModelRunDetailsDropdownOpen(false)}
          >
            {availableModelRunDetails.map(
              ({ modelRunName, targetDate }, index) => (
                <DropdownMenuButton
                  key={`${targetDate}_${modelRunName}_${index}`}
                  identifier={`model-run-${targetDate}_${modelRunName}`}
                  buttonClasses="flex items-center"
                  onClick={() => {
                    dispatch(
                      setSelectedModelRunDetails({
                        modelRunName,
                        targetDate
                      })
                    );
                    setModelRunDetailsDropdownOpen(false);
                  }}
                >
                  <span>{`${targetDate} ${modelRunName}`}</span>
                </DropdownMenuButton>
              )
            )}
          </DropdownMenu>
        ) : (
          <p className="text-error-300">No Model Runs available</p>
        )}
      </div>
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Available Segments:</label>
          {availableSegments.length > 0 ? (
            <DropdownMenu
              isDropdownOpen={isSegmentSelectorDropdownOpen}
              containerClasses="flex justify-between relative w-60 mr-3"
              buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 w-60 flex-row-reverse px-3 py-2 bg-white"
              buttonIconClasses="ra-icons-generic-down-arrow text-bottom text-xs flex"
              buttonLabel={selectedSegmentKey ?? 'Select Segment'}
              buttonLabelClasses=""
              hasExtraButtonIconClasses={false}
              dropdownMenuClasses="z-40 top-11 w-60"
              onDropdownClicked={() =>
                setSegmentSelectorDropdownOpen(!isSegmentSelectorDropdownOpen)
              }
              onClickAway={() => setSegmentSelectorDropdownOpen(false)}
            >
              {availableSegments.map((segmentKey, index) => (
                <DropdownMenuButton
                  key={`${segmentKey}_${index}`}
                  identifier={`segment-${segmentKey}`}
                  buttonClasses="flex items-center"
                  onClick={() => {
                    dispatch(setSelectedSegmentKey(segmentKey));
                    setSegmentSelectorDropdownOpen(false);
                  }}
                >
                  <span>{segmentKey}</span>
                </DropdownMenuButton>
              ))}
            </DropdownMenu>
          ) : (
            <p className="text-error-300">No segments available</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Available Time Ranges:</label>
          {availableTimeTypes.length > 0 ? (
            <DropdownMenu
              isDropdownOpen={isTimeRangeSelectorDropdownOpen}
              containerClasses="flex justify-between relative w-60 mr-3"
              buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 w-60 flex-row-reverse px-3 py-2 bg-white"
              buttonIconClasses="ra-icons-generic-down-arrow text-bottom text-xs flex"
              buttonLabel={selectedTimeType ?? 'Select Time Range'}
              buttonLabelClasses=""
              hasExtraButtonIconClasses={false}
              dropdownMenuClasses="z-40 top-11 w-60"
              onDropdownClicked={() =>
                setTimeRangeSelectorDropdownOpen(
                  !isTimeRangeSelectorDropdownOpen
                )
              }
              onClickAway={() => setTimeRangeSelectorDropdownOpen(false)}
            >
              {availableTimeTypes.map((timeType, index) => (
                <DropdownMenuButton
                  key={`${timeType}_${index}`}
                  identifier={`time-type-${timeType}`}
                  buttonClasses="flex items-center"
                  onClick={() => {
                    dispatch(setSelectedTimeType(timeType));
                    setTimeRangeSelectorDropdownOpen(false);
                  }}
                >
                  <span>{timeType}</span>
                </DropdownMenuButton>
              ))}
            </DropdownMenu>
          ) : (
            <p className="text-error-300">No Time Ranges available</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">Available Model Types:</label>
          {groupedFullBudgetScenariosForDropdown.length > 0 ? (
            <>
              <div className="relative flex items-start gap-4">
                <DropdownMenu
                  isDropdownOpen={isBudgetScenarioSelectorDropdownOpen}
                  containerClasses="flex justify-between relative w-120 mr-3"
                  buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 w-120 flex-row-reverse"
                  buttonIconClasses="ml-2 ra-icons-generic-down-arrow text-bottom text-xs flex"
                  buttonLabel={selectedBudgetScenarioLabel}
                  buttonLabelClasses=""
                  hasExtraButtonIconClasses={false}
                  dropdownMenuClasses="z-40 top-11 w-120"
                  onDropdownClicked={() =>
                    setBudgetScenarioSelectorDropdownOpen(
                      !isBudgetScenarioSelectorDropdownOpen
                    )
                  }
                  onClickAway={() =>
                    setBudgetScenarioSelectorDropdownOpen(false)
                  }
                >
                  <>
                    {' '}
                    {groupedFullBudgetScenariosForDropdown.map(
                      (budgetScenarioGroup, index) => {
                        return (
                          <Fragment
                            key={`budget_scenario_selector_group_${index}`}
                          >
                            <div
                              key={`${budgetScenarioGroup[0]}_${index}`}
                              className="text-sm pl-1 border-b border-neutral-200 mt-3 text-neutral-500"
                            >
                              {budgetScenarioGroup[0]}
                            </div>
                            {budgetScenarioGroup[1].map(
                              (budgetScenario, index) => {
                                const isChecked =
                                  selectedBudgetScenario?.budgetId ===
                                    budgetScenario.budgetId &&
                                  selectedBudgetScenario?.scenarioName ===
                                    budgetScenario.scenarioName;

                                return (
                                  <DropdownMenuButton
                                    key={`${budgetScenario.scenarioName}_${index}`}
                                    identifier={`model-type-${budgetScenario.scenarioName}`}
                                    buttonClasses="flex items-center w-full gap-2"
                                    onClick={() => {
                                      dispatch(
                                        setSelectedBudgetScenario({
                                          budgetId: budgetScenario.budgetId,
                                          scenarioName:
                                            budgetScenario.scenarioName
                                        })
                                      );
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      readOnly
                                    />
                                    <span>{budgetScenario.scenarioName}</span>
                                  </DropdownMenuButton>
                                );
                              }
                            )}
                          </Fragment>
                        );
                      }
                    )}
                  </>
                </DropdownMenu>
              </div>
            </>
          ) : (
            <p className="text-error-300">No Budget Scenarios available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignMmmControls;
